import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTokenRegion } from '@/helpers/getRegion';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { getPlatform } from '@/lib/analytics/legacy-sender-context';
import { type Dispatch, type RootState } from '@/store';
import { logger } from '@/utils/logger/logger';
import { extractIdpSubFromAccessToken } from '@heidi/auth/tokens';
import { NON_CANONICAL_REGION, toValidRegion } from '@heidi/utils';

// Writes the token region into `auth.userRegion`, which `getRegion()` reads
// above the raw claim. `useTokenRegion` normalises every source first.
export function useSetUserRegionFromToken() {
  const dispatch = useDispatch<Dispatch>();
  const region = useTokenRegion();

  useEffect(() => {
    if (region) {
      dispatch.auth.setUserRegion(region);
    }
  }, [dispatch.auth, region]);
}

// Records once per account (keyed by token subject) that the broker disagrees
// with the token region; neither the subject nor the raw broker value is sent.
// Must mount inside `KindePropertiesQueryProvider`, or the broker read is empty.
export function useRecordRegionSourceDivergence() {
  const tokenRegion = useTokenRegion();
  const brokerRegion = useQueryKindeProperties().data?.region ?? null;
  const token = useSelector((state: RootState) => state.auth.token);
  const accountKey = token ? extractIdpSubFromAccessToken(token) : undefined;
  const loggedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!tokenRegion || brokerRegion == null || !accountKey) return;
    const canonicalBrokerRegion = toValidRegion(brokerRegion);
    if (canonicalBrokerRegion === tokenRegion) return;
    if (loggedForRef.current === accountKey) return;

    loggedForRef.current = accountKey;
    // Bound through a child logger so the fields land as top-level attributes.
    logger
      .child({
        tokenRegion,
        brokerRegion: canonicalBrokerRegion ?? NON_CANONICAL_REGION,
        platform: getPlatform() === 'desktop' ? 'desktop' : 'web',
        regionsMatch: false,
      })
      .warn('[region] token region differs from identity broker region');
  }, [brokerRegion, tokenRegion, accountKey]);
}
